import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Loader2, Sparkles, X, ArrowRight } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

/**
 * OnboardingChecklist — dismissible "get started" card. Automatic steps flip
 * themselves; the admin-authored plan items are ticked by the member.
 */
export function OnboardingChecklist() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/onboarding/checklist");
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    if (item.kind === "auto") {
      if (item.link) navigate(item.link);
      return;
    }
    setBusy(item.item_id);
    try {
      await client.post(`/onboarding/items/${item.item_id}/toggle`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async () => {
    setHidden(true);
    try { await client.post("/onboarding/dismiss"); } catch { /* non-blocking */ }
  };

  if (!data || hidden || data.dismissed) return null;
  if (data.percent >= 100) return null;

  const remaining = data.items.filter((i) => !i.done);

  return (
    <Card id="dashboard-onboarding-card" className="relative overflow-hidden rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="onboarding-checklist">
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 end-3 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={t("memberOnboarding.dismiss", { defaultValue: "Hide checklist" })}
        data-testid="dashboard-onboarding-dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 pe-6">
          <h3 className="font-display font-semibold">
            {t("memberOnboarding.title", { defaultValue: "Get set up" })}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("memberOnboarding.subtitle", { defaultValue: "A few steps to unlock the full value of your account." })}
          </p>
        </div>
        <span className="font-mono text-xs text-muted-foreground shrink-0" data-testid="dashboard-onboarding-progress">
          {data.done}/{data.total}
        </span>
      </div>

      <Progress value={data.percent} className="h-1.5" />

      {/* Guided "start here" step — the single next thing to do. */}
      {remaining.length > 0 && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5" data-testid="onboarding-start-here">
          <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            {t("memberOnboarding.stepOf", {
              current: data.done + 1, total: data.total,
              defaultValue: `Step ${data.done + 1} of ${data.total}`,
            })}
          </p>
          <p className="text-sm font-semibold mt-1" data-testid="onboarding-start-here-title">
            {(isAr && remaining[0].title_ar) || remaining[0].title}
          </p>
          {((isAr && remaining[0].description_ar) || remaining[0].description) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {(isAr && remaining[0].description_ar) || remaining[0].description}
            </p>
          )}
          <Button
            size="sm"
            className="rounded-xl gap-2 mt-2.5 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
            onClick={() => (remaining[0].link ? navigate(remaining[0].link) : toggle(remaining[0]))}
            data-testid="onboarding-start-here-cta"
          >
            {remaining[0].link
              ? t("memberOnboarding.startNow", { defaultValue: "Do this now" })
              : t("memberOnboarding.markDone", { defaultValue: "Mark as done" })}
            <ArrowRight className="h-3.5 w-3.5 rtl-flip" />
          </Button>
        </div>
      )}

      <ul className="space-y-1.5">
        {data.items.map((item) => (
          <li key={item.item_id} className="flex items-start gap-3 rounded-xl px-2 py-1.5 hover:bg-accent/40 transition-colors" data-testid={`onboarding-item-${item.item_id}`}>
            <button
              type="button"
              onClick={() => toggle(item)}
              disabled={busy === item.item_id}
              className={item.done ? "text-primary mt-0.5" : "text-muted-foreground hover:text-primary mt-0.5"}
              aria-label={item.done ? "Completed" : "Mark done"}
              data-testid={`onboarding-toggle-${item.item_id}`}
            >
              {busy === item.item_id ? <Loader2 className="h-4 w-4 animate-spin" /> : item.done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${item.done ? "line-through text-muted-foreground" : "font-medium"}`}>{(isAr && item.title_ar) || item.title}</p>
              {!item.done && ((isAr && item.description_ar) || item.description) && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{(isAr && item.description_ar) || item.description}</p>
              )}
            </div>
            {!item.done && item.link && (
              <Button
                size="sm"
                variant="ghost"
                className="rounded-lg h-7 gap-1 text-xs shrink-0"
                onClick={() => navigate(item.link)}
                data-testid={`onboarding-go-${item.item_id}`}
              >
                {t("memberOnboarding.go", { defaultValue: "Open" })} <ArrowRight className="h-3 w-3 rtl-flip" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {remaining.length === 0 && (
        <p className="text-xs text-primary font-medium">
          {t("memberOnboarding.allDone", { defaultValue: "All set — nice work." })}
        </p>
      )}
    </Card>
  );
}

export default OnboardingChecklist;
